import { useMemo } from 'react'

import { useQuery } from 'modules/core/hooks/useQuery'

import { stringToDate } from 'modules/core/lib/dates'
import formatCurrency from 'modules/core/lib/formatCurrency'
import i18n from 'modules/core/lib/i18n'

import {
  IncidenceAdditionalCompensationChangesDocument,
  type IncidenceAdditionalCompensationChangesQuery,
} from 'generated/resources/graphql'

export type AdditionalCompNode =
  IncidenceAdditionalCompensationChangesQuery['contracts']['additionalCompensationsConnection']['nodes'][number]

export interface AdditionalCompensationChangeItem {
  id: string
  label: string
  oldValue: string | null
  newValue: string | null
}

export const formatAdditionalCompDisplay = (
  comp: AdditionalCompNode,
  legalEntityId?: number,
  includeFirstPayment?: boolean
): string | null => {
  const conceptName = comp.payrollConcept?.label ?? comp.description ?? ''
  const { amountStrategy } = comp

  let displayValue: string

  if (amountStrategy?.__typename === 'ContractsFixedAmountStrategy') {
    if (amountStrategy.amount == null) return conceptName || null

    const formattedAmount = formatCurrency(amountStrategy.amount, '', null, legalEntityId)
    displayValue = i18n.t('bookkeepers.incidence.details.compensation_display.fixed', {
      amount: formattedAmount,
      name: conceptName,
    })
  } else if (amountStrategy?.__typename === 'ContractsVariableAmountStrategy') {
    if (amountStrategy.upperLimit != null) {
      const formattedAmount = formatCurrency(amountStrategy.upperLimit, '', null, legalEntityId)
      displayValue = i18n.t('bookkeepers.incidence.details.compensation_display.up_to', {
        amount: formattedAmount,
        name: conceptName,
      })
    } else {
      displayValue = i18n.t('bookkeepers.incidence.details.compensation_display.no_limit', {
        name: conceptName,
      })
    }
  } else {
    return conceptName || null
  }

  if (includeFirstPayment && comp.firstPaymentOn) {
    const date = stringToDate(comp.firstPaymentOn, 'backend')
    displayValue += ` ${i18n.t('bookkeepers.incidence.details.compensation_display.with_first_payment', { date: date.format('MM/YYYY') })}`
  }

  return displayValue
}

const getMatchKey = (comp: AdditionalCompNode): string | null => {
  if (comp.payrollConcept?.id != null) return `concept:${comp.payrollConcept.id}`
  if (comp.description != null) return `desc:${comp.description}`
  return null
}

export const buildAdditionalCompensationChanges = (
  currentComps: AdditionalCompNode[],
  previousComps: AdditionalCompNode[],
  legalEntityId?: number
): AdditionalCompensationChangeItem[] => {
  const changes: AdditionalCompensationChangeItem[] = []
  const label = i18n.t('bookkeepers.incidence.details.additional_compensation_label')

  const previousByKey = new Map<string, AdditionalCompNode>()
  const unmatchedPrevious = new Set<number>()

  for (const prev of previousComps) {
    const key = getMatchKey(prev)
    if (key != null && !previousByKey.has(key)) {
      previousByKey.set(key, prev)
    }
    unmatchedPrevious.add(prev.id)
  }

  for (const current of currentComps) {
    const key = getMatchKey(current)
    const previous = key != null ? previousByKey.get(key) : undefined

    if (!previous || !unmatchedPrevious.has(previous.id)) {
      changes.push({
        id: `added-${current.id}`,
        label,
        oldValue: null,
        newValue: formatAdditionalCompDisplay(current, legalEntityId, true),
      })
    } else {
      unmatchedPrevious.delete(previous.id)

      const currentForComparison = formatAdditionalCompDisplay(current, legalEntityId)
      const previousForComparison = formatAdditionalCompDisplay(previous, legalEntityId)

      if (currentForComparison !== previousForComparison) {
        changes.push({
          id: `updated-${current.id}`,
          label,
          oldValue: formatAdditionalCompDisplay(previous, legalEntityId, true),
          newValue: formatAdditionalCompDisplay(current, legalEntityId, true),
        })
      }
    }
  }

  for (const previous of previousComps) {
    if (!unmatchedPrevious.has(previous.id)) continue

    changes.push({
      id: `deleted-${previous.id}`,
      label,
      oldValue: formatAdditionalCompDisplay(previous, legalEntityId, true),
      newValue: null,
    })
  }

  return changes
}

const useAdditionalCompensationChanges = (
  currentContractVersionId: number | null,
  previousContractVersionId: number | null,
  legalEntityId?: number
): { loading: boolean; changes: AdditionalCompensationChangeItem[] } => {
  const contractVersionIds = useMemo(() => {
    const ids: number[] = []
    if (currentContractVersionId != null) ids.push(currentContractVersionId)
    if (previousContractVersionId != null) ids.push(previousContractVersionId)
    return ids
  }, [currentContractVersionId, previousContractVersionId])

  const { data, loading } = useQuery(IncidenceAdditionalCompensationChangesDocument, {
    variables: { contractVersionIds },
    skip: contractVersionIds.length === 0,
  })

  const changes = useMemo(() => {
    if (!data) return []

    const nodes = data.contracts.additionalCompensationsConnection.nodes
    const currentComps = nodes.filter((n) => n.contractVersionId === currentContractVersionId)
    const previousComps = nodes.filter((n) => n.contractVersionId === previousContractVersionId)

    return buildAdditionalCompensationChanges(currentComps, previousComps, legalEntityId)
  }, [data, currentContractVersionId, previousContractVersionId, legalEntityId])

  return { loading, changes }
}

export default useAdditionalCompensationChanges
